/**
 * Who runs the service, as stated in the Terms of Use and the Privacy Policy.
 *
 * `legalName`, `address` and `companyId` are required by EU consumer and
 * GDPR rules to identify the operator, and `registerFile` by § 435 of the
 * Civil Code for a company in the Commercial Register. They are rendered only
 * when filled in, so fill them before relying on these documents.
 */
export const LEGAL_OPERATOR = {
  brand: 'InMyResume',
  website: 'https://inmyresume.com',
  email: 'info@inmyresume.com',
  /** Name of the natural person or company that operates the site. */
  legalName: 'ZakWeb s.r.o.',
  /** Registered address. Same as on the contact page (i18n contact.contactInfo). */
  address: 'Korunní 2569/108, Vinohrady, 101 00 Praha 10',
  /** Business ID (IČO). Same as on the contact page. */
  companyId: '30129494',
  /** File number in the Commercial Register kept by the Municipal Court in Prague. Same as on the contact page. */
  registerFile: 'C 457015',
  country: 'Czech Republic',
  /**
   * VAT status. Not a VAT payer and not yet an identified person
   * (identifikovaná osoba). Buying a service from a business in another EU
   * state (an EU partner's commission, EU ads) makes the company one, with 15
   * days to register: then set 'identified-person' and vatId, and bump
   * AFFILIATE_TERMS_VERSION, since the affiliate terms quote both.
   */
  vatStatus: 'none' as 'payer' | 'identified-person' | 'none',
  /** VAT number (DIČ) under which we are registered, e.g. CZ12345678. */
  vatId: '',
  supervisoryAuthority: {
    name: 'Office for Personal Data Protection (Úřad pro ochranu osobních údajů)',
    address: 'Pplk. Sochora 27, 170 00 Prague 7, Czech Republic',
    url: 'https://uoou.gov.cz',
  },
} as const

/** Shown as "Last updated" on both documents. Bump it whenever either text changes. */
export const LEGAL_LAST_UPDATED = '2026-10-30'

/** One Markdown-inline paragraph that identifies the operator with whatever is filled in. */
/** The VAT status in words, for the affiliate terms and payout statements. */
export function operatorVatLine(): string {
  const o = LEGAL_OPERATOR
  const id = o.vatId ? ` under VAT number ${o.vatId}` : ''
  if (o.vatStatus === 'payer') return `${o.legalName} is registered for VAT${id}.`
  if (o.vatStatus === 'identified-person') return `${o.legalName} is not a VAT payer and is registered in the Czech Republic as an identified person for VAT${id}.`
  return `${o.legalName} is not registered for VAT.`
}

export function operatorLine(): string {
  const o = LEGAL_OPERATOR
  const parts = [
    o.legalName || `${o.brand} (${o.website.replace('https://', '')})`,
    o.address,
    o.companyId && `Business ID: ${o.companyId}`,
    o.registerFile && `registered in the Commercial Register kept by the Municipal Court in Prague, file ${o.registerFile}`,
  ].filter(Boolean)
  return `${parts.join(', ')}. Contact: [${o.email}](mailto:${o.email}).`
}

/** operatorVatLine() in Czech, for the Czech versions of the documents. */
export function operatorVatLineCs(): string {
  const o = LEGAL_OPERATOR
  const id = o.vatId ? ` pod DIČ ${o.vatId}` : ''
  if (o.vatStatus === 'payer') return `${o.legalName} je plátcem DPH${id}.`
  if (o.vatStatus === 'identified-person') return `${o.legalName} není plátcem DPH, ale v České republice má registraci k DPH jako identifikovaná osoba${id}.`
  return `${o.legalName} nemá registraci k DPH.`
}

/** operatorLine() in Czech. */
export function operatorLineCs(): string {
  const o = LEGAL_OPERATOR
  const parts = [
    o.legalName || `${o.brand} (${o.website.replace('https://', '')})`,
    o.address,
    o.companyId && `IČO: ${o.companyId}`,
    o.registerFile && `zapsaná v obchodním rejstříku vedeném Městským soudem v Praze, sp. zn. ${o.registerFile}`,
  ].filter(Boolean)
  return `${parts.join(', ')}. Kontakt: [${o.email}](mailto:${o.email}).`
}

/** The supervisory authority as the Czech documents name it. */
export const SUPERVISORY_AUTHORITY_CS = {
  name: 'Úřad pro ochranu osobních údajů',
  address: 'Pplk. Sochora 27, 170 00 Praha 7',
} as const

/**
 * Opens every Czech version: the English text is the one that binds, so a
 * slip in translation cannot change what was agreed.
 */
export const CS_TRANSLATION_NOTE = '*Toto je překlad anglického znění, který slouží pro vaše pohodlí. V případě rozporu má přednost anglické znění.*'

/** operatorVatLine() in German, for the German versions of the documents. */
export function operatorVatLineDe(): string {
  const o = LEGAL_OPERATOR
  const id = o.vatId ? ` unter der USt-IdNr. ${o.vatId}` : ''
  if (o.vatStatus === 'payer') return `${o.legalName} ist umsatzsteuerlich registriert${id}.`
  if (o.vatStatus === 'identified-person') return `${o.legalName} ist kein Umsatzsteuerzahler und in der Tschechischen Republik als sogenannte identifizierte Person für die Umsatzsteuer registriert${id}.`
  return `${o.legalName} ist nicht umsatzsteuerlich registriert.`
}

/** operatorLine() in German. */
export function operatorLineDe(): string {
  const o = LEGAL_OPERATOR
  const parts = [
    o.legalName || `${o.brand} (${o.website.replace('https://', '')})`,
    o.address,
    'Tschechische Republik',
    o.companyId && `Firmennummer (IČO): ${o.companyId}`,
    o.registerFile && `eingetragen im Handelsregister beim Stadtgericht Prag, Aktenzeichen ${o.registerFile}`,
  ].filter(Boolean)
  return `${parts.join(', ')}. Kontakt: [${o.email}](mailto:${o.email}).`
}

/** The supervisory authority as the German documents name it. */
export const SUPERVISORY_AUTHORITY_DE = {
  name: 'Amt für den Schutz personenbezogener Daten (Úřad pro ochranu osobních údajů)',
  address: 'Pplk. Sochora 27, 170 00 Prag 7, Tschechische Republik',
} as const

/** Opens every German version, like CS_TRANSLATION_NOTE: the English text binds. */
export const DE_TRANSLATION_NOTE = '*Dies ist eine Übersetzung der englischen Fassung, die Ihrer Bequemlichkeit dient. Im Fall von Abweichungen gilt die englische Fassung.*'

/** operatorVatLine() in Polish, for the Polish versions of the documents. */
export function operatorVatLinePl(): string {
  const o = LEGAL_OPERATOR
  const id = o.vatId ? ` pod numerem VAT ${o.vatId}` : ''
  if (o.vatStatus === 'payer') return `${o.legalName} jest zarejestrowanym podatnikiem VAT${id}.`
  if (o.vatStatus === 'identified-person') return `${o.legalName} nie jest płatnikiem VAT, a w Republice Czeskiej jest zarejestrowana do VAT jako tzw. osoba zidentyfikowana${id}.`
  return `${o.legalName} nie jest zarejestrowana jako podatnik VAT.`
}

/** operatorLine() in Polish. */
export function operatorLinePl(): string {
  const o = LEGAL_OPERATOR
  const parts = [
    o.legalName || `${o.brand} (${o.website.replace('https://', '')})`,
    o.address,
    'Republika Czeska',
    o.companyId && `numer identyfikacyjny (IČO): ${o.companyId}`,
    o.registerFile && `wpisana do rejestru handlowego prowadzonego przez Sąd Miejski w Pradze, sygn. ${o.registerFile}`,
  ].filter(Boolean)
  return `${parts.join(', ')}. Kontakt: [${o.email}](mailto:${o.email}).`
}

/** The supervisory authority as the Polish documents name it. */
export const SUPERVISORY_AUTHORITY_PL = {
  name: 'Urząd Ochrony Danych Osobowych Republiki Czeskiej (Úřad pro ochranu osobních údajů)',
  address: 'Pplk. Sochora 27, 170 00 Praga 7, Republika Czeska',
} as const

/** Opens every Polish version, like CS_TRANSLATION_NOTE: the English text binds. */
export const PL_TRANSLATION_NOTE = '*To jest tłumaczenie wersji angielskiej, przygotowane dla Twojej wygody. W razie rozbieżności pierwszeństwo ma wersja angielska.*'

export type LegalBlock =
  /** A paragraph; Markdown inline syntax (links, bold) is allowed. */
  | string
  | { list: string[] }
  | { table: { head: string[]; rows: string[][] } }

export interface LegalSection {
  id: string
  title: string
  blocks: LegalBlock[]
}

export interface LegalDocument {
  intro: LegalBlock[]
  sections: LegalSection[]
}
